import React, { useState, useEffect, useRef } from 'react';
import './UscitaDetail.css';
import { Uscita, Rifornimento, UscitaWritableFields } from '../../services/api';
import { enqueueUscitaUpdate } from '../../services/writeQueue';

type OrarioKey = 'apertura' | 'inizioPausa' | 'finePausa' | 'chiusura';

// Definizione interfaccia CarburanteEntry per gestione locale
export interface CarburanteEntry {
  id: string;
  targa?: string;
  euroPerLitro: number;
  litri: number;
  totaleEuro: number;
}

interface UscitaDetailProps {
  uscita: Uscita; // Usa direttamente Uscita con rifornimenti[]
  onClose: () => void;
  className?: string;
  isAdmin?: boolean;
}

const UscitaDetail: React.FC<UscitaDetailProps> = ({ uscita, onClose, className, isAdmin = false }) => {
  // Funzione per convertire Rifornimento in CarburanteEntry
  const rifornimentoToCarburanteEntry = (rifornimento: Rifornimento): CarburanteEntry => {
    const litri = rifornimento.mob_rifornimento_costo_litro > 0 
      ? parseFloat((rifornimento.mob_rifornimento_costo_tot / rifornimento.mob_rifornimento_costo_litro).toFixed(1))
      : 0;
    
    return {
      id: rifornimento.mob_rifornimento_id.toString(),
      targa: rifornimento.mob_auto_targa,
      euroPerLitro: rifornimento.mob_rifornimento_costo_litro,
      litri: litri,
      totaleEuro: rifornimento.mob_rifornimento_costo_tot
    };
  };

  // Funzione per convertire CarburanteEntry in Rifornimento
  const carburanteEntryToRifornimento = (carburanteEntry: CarburanteEntry): Rifornimento => {
    return {
      mob_rifornimento_id: parseInt(carburanteEntry.id),
      mob_auto_targa: carburanteEntry.targa || '',
      mob_rifornimento_costo_litro: carburanteEntry.euroPerLitro,
      mob_rifornimento_costo_tot: carburanteEntry.totaleEuro,
      mob_usc_id: uscita.mob_usc_id,
      recordId: 0
    };
  };

  // Inizializza carburanti dal backend se disponibili
  const getInitialCarburanti = (): CarburanteEntry[] => {
    if (uscita.rifornimento && uscita.rifornimento.length > 0) {
      return uscita.rifornimento.map(rifornimentoToCarburanteEntry);
    }
    return [];
  };

  // Stato per i carburanti (convertiti da rifornimenti backend)
  const [carburanti, setCarburanti] = useState<CarburanteEntry[]>(getInitialCarburanti());
  const [editableKm, setEditableKm] = useState<number | string>(uscita.usc_auto_km_totali_reali !== undefined ? uscita.usc_auto_km_totali_reali : '');
  const [noteSede, setNoteSede] = useState(uscita.mob_usc_note_x_cg || '');
  const [noteCapogruppo, setNoteCapogruppo] = useState(uscita.usc_note_da_cg || '');
  const [kmInputFocused, setKmInputFocused] = useState(false);
  // Valori orari impostati otticamente al click (subito visibili, senza attendere conferma da FM) —
  // la coda in writeQueue.ts gestisce l'invio/retry in background, in silenzio.
  const [localOrari, setLocalOrari] = useState<Partial<Record<OrarioKey, string>>>({});

  const noteSedeRef = useRef<HTMLTextAreaElement>(null);
  const noteCapogruppoRef = useRef<HTMLTextAreaElement>(null);

  // Effetto per aggiornare lo stato locale se la prop uscita cambia
  useEffect(() => {
    console.log('[UscitaDetail] Aggiornamento uscita props:', uscita.rifornimento);
    setCarburanti(getInitialCarburanti());
    setEditableKm(uscita.usc_auto_km_totali_reali !== undefined ? uscita.usc_auto_km_totali_reali : '');
    setNoteSede(uscita.mob_usc_note_x_cg || '');
    setNoteCapogruppo(uscita.usc_note_da_cg || '');
  }, [uscita]);

  // AGGIUNTO: useEffect per auto-resize della textarea noteSede
  useEffect(() => {
    if (noteSedeRef.current) {
      noteSedeRef.current.style.height = 'auto';
      noteSedeRef.current.style.height = `${noteSedeRef.current.scrollHeight}px`;
    }
  }, [noteSede]);

  // AGGIUNTO: useEffect per auto-resize della textarea noteCapogruppo
  useEffect(() => {
    if (noteCapogruppoRef.current) {
      noteCapogruppoRef.current.style.height = 'auto';
      noteCapogruppoRef.current.style.height = `${noteCapogruppoRef.current.scrollHeight}px`;
    }
  }, [noteCapogruppo]);

  const handleClose = () => {
    console.log("[UscitaDetail] Pulsante Indietro cliccato - chiamando onClose");
    onClose();
  };

  const formatDisplayDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Data non disponibile';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Funzione helper per convertire dal formato "H.MM.SS" a "HH:MM"
  const formatTimeStringToDisplay = (timeString: string | undefined): string => {
    console.log('[UscitaDetail] formatTimeStringToDisplay input:', timeString, 'tipo:', typeof timeString);
    
    if (!timeString || timeString.trim() === '') {
      console.log('[UscitaDetail] formatTimeStringToDisplay: stringa vuota o undefined');
      return '--:--'; // Valore di fallback
    }
    
    try {
      // Formato atteso: "H.MM.SS" o "HH.MM.SS"
      const parts = timeString.split('.');
      console.log('[UscitaDetail] formatTimeStringToDisplay parti split:', parts);
      
      if (parts.length !== 3) {
        console.log('[UscitaDetail] formatTimeStringToDisplay: numero parti non valido');
        return '--:--';
      }
      
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1];
      const result = `${hours}:${minutes}`;
      
      console.log('[UscitaDetail] formatTimeStringToDisplay result:', result);
      return result;
    } catch (error) {
      console.error('[UscitaDetail] Errore nel parsing dell\'orario:', timeString, error);
      return '--:--';
    }
  };

  // Handlers per i carburanti
  const aggiungiCarburante = () => {
    const nuovoId = Date.now().toString();
    const nuovoCarburante: CarburanteEntry = {
      id: nuovoId,
      targa: uscita.mob_usc_auto_targa || '',
      euroPerLitro: 0,
      litri: 0,
      totaleEuro: 0
    };
    
    setCarburanti(prev => [...prev, nuovoCarburante]);
    console.log('[UscitaDetail] Aggiunto nuovo carburante:', nuovoCarburante);
    // TODO: Salvare nel backend
  };

  const aggiornaCarburante = (index: number, campo: keyof CarburanteEntry, valore: string) => {
    const nuoviCarburanti = [...carburanti];
    const carburanteDaAggiornare = { ...nuoviCarburanti[index] };

    if (campo === 'id') return;

    if (campo === 'targa') {
      carburanteDaAggiornare[campo] = valore;
    } else {
      const valoreNumerico = parseFloat(valore);
      (carburanteDaAggiornare[campo] as number) = isNaN(valoreNumerico) ? 0 : valoreNumerico;

      // Calcola automaticamente i litri se cambiano prezzo per litro o totale euro
      if (campo === 'euroPerLitro' || campo === 'totaleEuro') {
        if (carburanteDaAggiornare.euroPerLitro > 0 && carburanteDaAggiornare.totaleEuro > 0) {
          carburanteDaAggiornare.litri = parseFloat((carburanteDaAggiornare.totaleEuro / carburanteDaAggiornare.euroPerLitro).toFixed(1));
        } else {
          carburanteDaAggiornare.litri = 0;
        }
      }
    }
    
    nuoviCarburanti[index] = carburanteDaAggiornare;
    setCarburanti(nuoviCarburanti);
    console.log('[UscitaDetail] Aggiornato carburante:', carburanteDaAggiornare);
    // TODO: Salvare nel backend - convertire in Rifornimento e inviare
  };

  const rimuoviCarburante = (idToRemove: string) => {
    setCarburanti(prev => prev.filter(r => r.id !== idToRemove));
    console.log('[UscitaDetail] Rimosso carburante con id:', idToRemove);
    // TODO: Rimuovere dal backend
  };

  // Ora corrente in formato FM (HH:MM:SS, atteso dal campo Data API) e in formato display (HH:MM)
  const getCurrentTimeFm = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  };

  const getCurrentTimeDisplay = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const buildOrarioFields = (key: OrarioKey, time: string, gps: string): Partial<UscitaWritableFields> => {
    switch (key) {
      case 'apertura': return { usc_ora_apertura: time, usc_ora_apertura_latlon: gps };
      case 'chiusura': return { usc_ora_chiusura: time, usc_ora_chiusura_latlon: gps };
      case 'inizioPausa': return { usc_ora_inizio_pausa: time };
      case 'finePausa': return { usc_ora_fine_pausa: time };
    }
  };

  // Click su Partenza/Inizio/Fine/Rientro: mostra subito il valore in UI (ottimistico), poi
  // tenta il GPS (best-effort, non blocca il punch se negato/non disponibile) e mette la
  // scrittura in coda — invio e retry avvengono in background, senza indicatori di stato.
  const handleOrarioClick = (key: OrarioKey) => {
    setLocalOrari(prev => ({ ...prev, [key]: getCurrentTimeDisplay() }));
    const time = getCurrentTimeFm();

    const send = (gps: string) => {
      void enqueueUscitaUpdate(uscita.recordId, buildOrarioFields(key, time, gps), uscita.modId);
    };

    if (!navigator.geolocation) {
      send('');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => send(`${pos.coords.latitude},${pos.coords.longitude}`),
      () => send(''),
      { timeout: 5000 }
    );
  };

  // Al blur della nota capogruppo: invia solo se il testo è cambiato rispetto a quello noto da FM.
  const handleNoteCapogruppoBlur = () => {
    if (noteCapogruppo !== (uscita.usc_note_da_cg || '')) {
      void enqueueUscitaUpdate(uscita.recordId, { usc_note_da_cg: noteCapogruppo }, uscita.modId);
    }
  };

  const handleKmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Permetti stringa vuota per cancellare, o converti in numero
    setEditableKm(value === '' ? '' : parseFloat(value));
    console.log('[UscitaDetail] Aggiornato km:', value);
    // TODO: Salvare nel backend
  };

  const handleKmFocus = () => {
    setKmInputFocused(true);
  };

  // Al blur (l'operatore ha finito di modificare il campo): invia solo se il valore è
  // davvero cambiato rispetto a quello noto da FM, per non accodare scritture inutili
  // ogni volta che si entra/esce dal campo senza modificarlo.
  const handleKmBlur = () => {
    setKmInputFocused(false);
    const km = editableKm === '' ? 0 : Number(editableKm);
    if (km !== (uscita.usc_auto_km_totali_reali ?? 0)) {
      void enqueueUscitaUpdate(uscita.recordId, { usc_auto_km_totali_reali: km }, uscita.modId);
    }
  };

  // Funzione per formattare il valore mostrato nell'input km
  const getKmDisplayValue = () => {
    console.log('[UscitaDetail] getKmDisplayValue - editableKm:', editableKm, 'tipo:', typeof editableKm, 'kmInputFocused:', kmInputFocused);
    
    if (kmInputFocused) {
      // In focus: mostra solo il numero
      console.log('[UscitaDetail] getKmDisplayValue - in focus, return:', editableKm);
      return editableKm;
    } else {
      // Non in focus: mostra "Km (numero)" se c'è un valore
      if (editableKm !== '' && editableKm !== 0) {
        const result = `Km ${editableKm}`;
        console.log('[UscitaDetail] getKmDisplayValue - not in focus with value, return:', result);
        return result;
      } else {
        console.log('[UscitaDetail] getKmDisplayValue - not in focus without value, return empty');
        return '';
      }
    }
  };
  
  console.log("[UscitaDetail] RENDER, props.uscita:", uscita); // DEBUG
  console.log("[UscitaDetail] RENDER, rifornimenti backend:", uscita.rifornimento); // DEBUG
  console.log("[UscitaDetail] RENDER, carburanti locali:", carburanti); // DEBUG
  console.log("[UscitaDetail] RENDER, className:", className); // DEBUG
  
  if (!uscita) {
    console.log("[UscitaDetail] Uscita prop è null, non rendo nulla."); // DEBUG
    return null; // Non dovrebbe succedere se la logica in Uscite.tsx è corretta
  }

  return (
    <div className={`widget-card uscita-detail-widget ${className || ''}`}>
      {/* Header standard del widget con X di chiusura */}
      <div className="widget-header">
        <h3>{uscita.gruppo_cg_nome} - Gruppo {uscita.mob_usc_num}</h3>
        <button onClick={handleClose} className="widget-close-btn" aria-label="Chiudi widget dettaglio">
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      {/* Contenuto del widget */}
      <div className="widget-content">
        {/* Contenitore per i dettagli specifici dell'uscita */}
        <div className="uscita-detail-body">
          {/* Data Uscita Section */}
          <div className="detail-section-container uscita-detail-data-display"> {/* Applica entrambe le classi */}
            <div className="detail-section-header">
              <span>Data Uscita</span>
            </div>
            <div className="uscita-detail-data-content">
              {formatDisplayDate(uscita.mob_usc_data)}
            </div>
          </div>

          {/* Orari Section */}
          <div className="detail-section-container uscita-detail-orari">
            <div className="detail-section-header">
              <span>Gestione orari</span>
            </div>
            <div className="orari-content-wrapper"> {/* Nuovo wrapper per il contenuto effettivo */}
              {localOrari.apertura || (uscita.usc_ora_apertura && uscita.usc_ora_apertura.trim() !== '') ? (
                <div className="orario-display-container partenza-display">
                  <div className="orario-label">Partenza</div>
                  <div className="orario-value">{localOrari.apertura || formatTimeStringToDisplay(uscita.usc_ora_apertura)}</div>
                </div>
              ) : (
                <button className="orari-button partenza" onClick={() => handleOrarioClick('apertura')}>Partenza</button>
              )}

              <div className="orario-display-container pausa-display">
                <div className="orario-label">Pausa</div>
                <div className="pausa-split">
                  {localOrari.inizioPausa || (uscita.usc_ora_inizio_pausa && uscita.usc_ora_inizio_pausa.trim() !== '') ? (
                    <span className="orario-value">{localOrari.inizioPausa || formatTimeStringToDisplay(uscita.usc_ora_inizio_pausa)}</span>
                  ) : (
                    <button className="orari-button pausa-inizio" onClick={() => handleOrarioClick('inizioPausa')}>Inizio</button>
                  )}
                  {localOrari.finePausa || (uscita.usc_ora_fine_pausa && uscita.usc_ora_fine_pausa.trim() !== '') ? (
                    <span className="orario-value">{localOrari.finePausa || formatTimeStringToDisplay(uscita.usc_ora_fine_pausa)}</span>
                  ) : (
                    <button className="orari-button pausa-fine" onClick={() => handleOrarioClick('finePausa')}>Fine</button>
                  )}
                </div>
              </div>

              {localOrari.chiusura || (uscita.usc_ora_chiusura && uscita.usc_ora_chiusura.trim() !== '') ? (
                <div className="orario-display-container rientro-display">
                  <div className="orario-label">Rientro</div>
                  <div className="orario-value">{localOrari.chiusura || formatTimeStringToDisplay(uscita.usc_ora_chiusura)}</div>
                </div>
              ) : (
                <button className="orari-button rientro" onClick={() => handleOrarioClick('chiusura')}>Rientro</button>
              )}
            </div>
          </div>

          {/* Automezzo Section (ex Mezzo) */}
          <div className="detail-section-container uscita-detail-automezzo">
            <div className="detail-section-header">
              <span>Automezzo e km</span>
            </div>
            <div className="automezzo-content-wrapper">
              <div className="automezzo-data-row">
                <div className="automezzo-targa">{uscita.mob_usc_auto_targa || 'Non specificato'}</div>
                <div className="km-value">
                  <input
                    type={kmInputFocused ? "number" : "text"}
                    className="km-input" // Nuova classe per lo stile specifico
                    value={getKmDisplayValue()}
                    onChange={handleKmChange}
                    onFocus={handleKmFocus}
                    onBlur={handleKmBlur}
                    placeholder="N/D"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Carburante Section (ex Rifornimenti) */}
          <div className="detail-section-container uscita-detail-carburante"> 
            <div className="detail-section-header">
              <span>Carburante</span>
              <button onClick={aggiungiCarburante} className="add-carburante-btn" title="Aggiungi carburante">
                +
              </button>
            </div>
            <div className="carburante-content-wrapper"> 
              <table className="carburante-table"> 
                <thead>
                  <tr>
                    <th>Targa</th>
                    <th>€/lt</th>
                    <th>€ Tot.</th>
                    <th>Litri</th>
                    {isAdmin && <th>X</th>}
                  </tr>
                </thead>
                <tbody>
                  {carburanti.map((carb, index) => (
                    <tr key={carb.id}>
                      <td>
                        <input
                          type="text"
                          value={carb.targa !== undefined ? carb.targa : (uscita.mob_usc_auto_targa || '')}
                          onChange={(e) => aggiornaCarburante(index, 'targa', e.target.value)}
                          placeholder="Targa"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={carb.euroPerLitro}
                          onChange={(e) => aggiornaCarburante(index, 'euroPerLitro', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={carb.totaleEuro}
                          onChange={(e) => aggiornaCarburante(index, 'totaleEuro', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={carb.litri}
                          readOnly
                          className="readonly-input"
                        />
                      </td>
                      {isAdmin && (
                        <td>
                          <button 
                            onClick={() => rimuoviCarburante(carb.id)}
                            className="carburante-remove-btn"
                            title="Rimuovi carburante"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Contenitore per le sezioni Note */}
          <div className="uscita-detail-note-container">
            {/* Sezione Note su Uscita da Sede */}
            <div className="detail-section-container note-section"> 
              <div className="note-title"><span>Note da sede</span></div>
              <div className="note-content-wrapper"> 
                <textarea 
                  ref={noteSedeRef}
                  className="note-textarea"
                  value={noteSede}
                  onChange={(e) => setNoteSede(e.target.value)}
                  placeholder="Inserisci note dalla sede..."
                />
              </div>
            </div>

            {/* Sezione Note Responsabile */}
            <div className="detail-section-container note-section">
              <div className="note-title">
                <span>Note capogruppo</span>
              </div>
              <div className="note-content-wrapper">
                <textarea
                  ref={noteCapogruppoRef}
                  className="note-textarea"
                  placeholder="inserisci una nota..."
                  value={noteCapogruppo}
                  onChange={(e) => setNoteCapogruppo(e.target.value)}
                  onBlur={handleNoteCapogruppoBlur}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UscitaDetail; 